import { http, HttpError, setAdminToken } from "./http";
import { config } from "@/config";

export type AdminSession = {
  user: {
    id: number;
    username: string;
    display_name: string;
    user_type: "internal" | "provider" | "customer";
  };
  permissions: string[];
  scope: "admin";
  mfa_at: number | null;
};

export type LoginResponse = {
  token: string;
  token_type: "bearer";
  expires_in: number;
  scope: "admin";
  user: AdminSession["user"];
};

export async function login(username: string, password: string): Promise<LoginResponse> {
  if (config.useMocks) {
    const fake: LoginResponse = {
      token: "mock.admin.token",
      token_type: "bearer",
      expires_in: 1800,
      scope: "admin",
      user: { id: 1, username, display_name: username, user_type: "internal" },
    };
    setAdminToken(fake.token);
    return fake;
  }
  const res = await http<LoginResponse>("/admin/auth/login", {
    method: "POST",
    json: { username, password },
  });
  setAdminToken(res.token);
  return res;
}

export async function logout(): Promise<void> {
  try {
    await http("/admin/auth/logout", { method: "POST", json: {} });
  } catch {
    // Token may already be invalid — silent.
  } finally {
    setAdminToken(null);
  }
}

export async function fetchSession(): Promise<AdminSession | null> {
  if (config.useMocks) return mockSession();
  try {
    return await http<AdminSession>("/admin/_meta/session");
  } catch (e) {
    if (e instanceof HttpError && (e.status === 401 || e.status === 403)) return null;
    throw e;
  }
}

export function hasPermission(session: AdminSession | null, perm: string): boolean {
  if (!session) return false;
  return session.permissions.includes(perm) || session.permissions.includes("*");
}

function mockSession(): AdminSession {
  return {
    user: {
      id: 1,
      username: "mock-admin",
      display_name: "Mock Superadmin",
      user_type: "internal",
    },
    permissions: ["*"],
    scope: "admin",
    mfa_at: Date.now(),
  };
}
